import {
	isStreamAnswerPart,
	isStreamResponse,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import type {
	ParsedSSEMessage,
	StreamMessage,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import type {
	Conversation,
	StoreChatItem,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

import type { ChatStoreContainerProps } from './types';

type UpdateCurrentConversation = (data: Partial<Conversation>) => void;

export interface ReplayFlushOptions {
	conversationId: string;

	/**
	 * Captured just BEFORE the reconnect API call is made. SSE event IDs are
	 * server-side Unix timestamps (with an optional sequence suffix, e.g.
	 * "1775850341989-0"). Any event whose numeric prefix is < reconnectTimestamp
	 * was emitted before this reconnect began and is therefore a replay.
	 */
	reconnectTimestamp: number;

	getMessages: ChatStoreContainerProps['getMessages'];
	setMessages: ChatStoreContainerProps['setMessages'];
	processStreamResearch: ChatStoreContainerProps['processStreamResearch'];
	updateCurrentConversation: UpdateCurrentConversation;
}

type ProcessStreamResearchParams = Parameters<ChatStoreContainerProps['processStreamResearch']>[0];

export interface ReplayFlushResult {
	/**
	 * Wrap the source generator (from createReconnectableStream) with this before
	 * passing to processReconnectStreamMessage. Flushes all buffered store updates
	 * just before the first live chunk is yielded.
	 */
	wrapGenerator: (
		source: AsyncGenerator<ParsedSSEMessage<StreamMessage>>,
	) => AsyncGenerator<ParsedSSEMessage<StreamMessage>>;

	/** Pass instead of the real setMessages — writes are buffered during replay and committed as a single update on flush. */
	batchedSetMessages: ChatStoreContainerProps['setMessages'];

	/** Pass instead of the real getMessages — reads from the in-memory buffer during replay so read-then-write patterns stay coherent. */
	batchedGetMessages: ChatStoreContainerProps['getMessages'];

	/**
	 * Pass instead of the real updateCurrentConversation — patches are merged
	 * during replay (later keys win) and committed as a single update on flush.
	 * Pre-seeded with { traces: [] } so "Resuming response..." is always cleared.
	 */
	batchedUpdateCurrentConversation: UpdateCurrentConversation;

	/**
	 * Pass instead of the real processStreamResearch — calls are queued during
	 * replay and replayed synchronously on flush so the research store jumps
	 * directly to its final state in one render, skipping intermediate spinners.
	 *
	 * Queue is collapsed when ANSWER_PART or FINAL_RESPONSE is seen — research
	 * steps are not shown once the answer has started, so they're dropped to
	 * free memory.
	 */
	batchedProcessStreamResearch: ChatStoreContainerProps['processStreamResearch'];

	/**
	 * Returns true while replay chunks are being processed. Consumers with
	 * artificial delays (e.g. research step progress timers) can use this to
	 * skip them during replay.
	 */
	getIsReplaying: () => boolean;
}

/**
 * Wraps a reconnect stream generator to batch store updates during the "replay"
 * phase into a single flush just before the first live chunk is processed.
 *
 * Pipeline:
 *   createReconnectableStream  (network / reconnection)
 *     → withReplayFlush        (replay batching)
 *       → processReconnectStreamMessage  (store updates)
 *
 * Replay detection: SSE event IDs are server-side Unix timestamps with an
 * optional sequence suffix (e.g. "1775850341989-0"). We parse the numeric
 * prefix and compare to reconnectTimestamp. Chunks with no id or a non-numeric
 * id are treated as replay until a live chunk is seen.
 */
export function withReplayFlush({
	conversationId,
	reconnectTimestamp,
	getMessages,
	setMessages,
	processStreamResearch,
	updateCurrentConversation,
}: ReplayFlushOptions): ReplayFlushResult {
	// Lazily initialised on first setMessages write — avoids copying the full
	// messages array upfront when multiple tabs reconnect simultaneously.
	// replayBuffering=true + replayBuffer=null → buffering, no write yet
	// replayBuffering=false + replayBuffer=null → flushed
	let replayBuffer: StoreChatItem[] | null = null;
	let replayBuffering = true;

	// Pre-seeded with { traces: [] } so flush always clears "Resuming response..."
	// even if no clearTracesIfNotEmpty call fires during replay.
	// null means flushed.
	let conversationPatch: Parameters<UpdateCurrentConversation>[0] | null = { traces: [] };

	// null means flushed.
	let researchQueue: ProcessStreamResearchParams[] | null = [];

	const isBuffering = () => replayBuffering;

	const parseEventTime = (id: string): number => Number(id.split('-')[0]);

	const flush = () => {
		if (!isBuffering()) {
			return;
		}

		const buffered = replayBuffer;
		const patch = conversationPatch;
		const queued = researchQueue!;

		// Mark flushed before dispatching so re-entrant calls go live.
		replayBuffering = false;
		replayBuffer = null;
		conversationPatch = null;
		researchQueue = null;

		if (buffered !== null) {
			setMessages(conversationId, buffered);
		}

		if (patch !== null) {
			updateCurrentConversation(patch);
		}

		// react-sweet-state setState is synchronous — the research store reaches
		// its final state before React re-renders, giving one render with
		// researchCompleted=true and skipping intermediate spinner states.
		queued.forEach((params) => processStreamResearch(params));
	};

	const batchedSetMessages: ChatStoreContainerProps['setMessages'] = (convId, msgs) => {
		if (replayBuffering && convId === conversationId) {
			replayBuffer = msgs;
			return;
		}
		setMessages(convId, msgs);
	};

	const batchedGetMessages: ChatStoreContainerProps['getMessages'] = (convId) => {
		if (replayBuffering && convId === conversationId) {
			// Fall through to real store if no write has arrived yet.
			return replayBuffer ?? getMessages(convId);
		}
		return getMessages(convId);
	};

	const batchedUpdateCurrentConversation: UpdateCurrentConversation = (patch) => {
		if (conversationPatch !== null) {
			conversationPatch = { ...conversationPatch, ...patch };
			return;
		}
		updateCurrentConversation(patch);
	};

	const batchedProcessStreamResearch: ChatStoreContainerProps['processStreamResearch'] = (
		params,
	) => {
		if (researchQueue !== null) {
			if (isStreamResponse(params.streamMessage)) {
				// Stream was already complete — only FINAL_RESPONSE is needed to
				// set researchCompleted=true. Drop all accumulated step data.
				researchQueue = [params];
				return;
			}

			if (isStreamAnswerPart(params.streamMessage)) {
				// Answer has started — research steps were already shown. Drop all
				// accumulated steps; keep one ANSWER_PART to set hasReceivedAnswerPart=true.
				researchQueue = [params];
				return;
			}

			researchQueue.push(params);
			return;
		}
		processStreamResearch(params);
	};

	async function* replayFlushGenerator(
		source: AsyncGenerator<ParsedSSEMessage<StreamMessage>>,
	): AsyncGenerator<ParsedSSEMessage<StreamMessage>> {
		for await (const chunk of source) {
			if (isBuffering() && chunk.id) {
				const eventTime = parseEventTime(chunk.id);
				if (!isNaN(eventTime) && eventTime >= reconnectTimestamp) {
					flush();
				}
			}
			yield chunk;
		}
		// Stream ended while still in replay (response was complete on reconnect).
		flush();
	}

	return {
		wrapGenerator: replayFlushGenerator,
		batchedSetMessages,
		batchedGetMessages,
		batchedProcessStreamResearch,
		batchedUpdateCurrentConversation,
		getIsReplaying: isBuffering,
	};
}
